import { useEffect, useRef, useState } from 'react';
import type { Rapport } from '@arbeitsrapporte/shared';
import { api } from '../api';
import { Fehler } from '../bausteine/Meldung';
import { datumLesbar } from '../format';
import { navigiere } from '../router';
import { Positionstabelle } from './Rapport';

/**
 * Das Unterschriftenfeld – ein Canvas, mit dem Finger oder Stift. Das
 * Ergebnis geht als PNG zur API, zusammen mit dem Namen und dem Zeitpunkt.
 *
 * **Über dem Feld steht, was unterschrieben wird.** Der Kunde bekommt das
 * Gerät in die Hand und soll Datum, Arbeiten und Positionen sehen – nicht
 * blind einen Namenszug unter ein leeres Feld setzen. Dieselbe Tabelle wie
 * auf dem Rapport, mit derselben Summe.
 */
export function UnterschriftSeite({ id }: { id: string }) {
  const leinwand = useRef<HTMLCanvasElement>(null);
  const zeichnet = useRef(false);
  const [r, setR] = useState<Rapport | null>(null);
  const [leer, setLeer] = useState(true);
  const [name, setName] = useState('');
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    api
      .rapport(id)
      .then((geladen) => {
        setR(geladen);
        /* Der Ansprechpartner steht meist mit dem Namen da, der unterschreibt. */
        setName((n) => n || geladen.ansprechpartner);
      })
      .catch(setFehler);
  }, [id]);

  useEffect(() => {
    const c = leinwand.current;

    if (!c) return;

    const skala = window.devicePixelRatio || 1;
    const breite = Math.min(c.parentElement?.clientWidth ?? 600, 700);

    c.width = breite * skala;
    c.height = 220 * skala;
    c.style.width = `${breite}px`;
    c.style.height = '220px';

    const ctx = c.getContext('2d');

    if (ctx) {
      ctx.scale(skala, skala);
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#111';
    }
  }, [r]);

  const punkt = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();

    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const beginne = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = e.currentTarget.getContext('2d');

    if (!ctx) return;

    e.currentTarget.setPointerCapture(e.pointerId);
    zeichnet.current = true;
    const p = punkt(e);

    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const ziehe = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!zeichnet.current) return;

    const ctx = e.currentTarget.getContext('2d');

    if (!ctx) return;

    const p = punkt(e);

    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setLeer(false);
  };

  const beende = () => {
    zeichnet.current = false;
  };

  const loesche = () => {
    const c = leinwand.current;
    const ctx = c?.getContext('2d');

    if (c && ctx) {
      ctx.clearRect(0, 0, c.width, c.height);
      setLeer(true);
    }
  };

  const uebernehme = async () => {
    const c = leinwand.current;

    if (!c) return;

    setLaeuft(true);
    setFehler(null);

    try {
      const danach = await api.unterschreibe(id, {
        name: name.trim(),
        bild: c.toDataURL('image/png'),
        zeitpunkt: new Date().toISOString(),
      });

      navigiere(`/rapport/${danach.id}`);
    } catch (f) {
      setFehler(f);
      setLaeuft(false);
    }
  };

  if (fehler && !r) return <Fehler fehler={fehler} />;
  if (!r) return <p className="text-slate-600">Lädt …</p>;

  if (!r.erlaubt.aendern || r.status !== 'entwurf' || r.unterschrift) {
    return (
      <>
        <p className="mb-4 rounded border border-rose-300 bg-rose-50 px-3 py-2 text-rose-900">
          {r.unterschrift
            ? 'Dieser Rapport ist schon unterschrieben.'
            : 'Dieser Rapport lässt sich gerade nicht unterschreiben.'}
        </p>
        <a
          href={`/rapport/${r.id}`}
          className="text-slate-700 underline underline-offset-4 hover:text-slate-900"
        >
          Zurück zum Rapport
        </a>
      </>
    );
  }

  return (
    <>
      <h1 className="text-2xl font-semibold">Unterschrift des Kunden</h1>
      <p className="mb-4 text-slate-600">
        {r.nummer} · {r.kundeName || 'kein Kunde'} · {datumLesbar(r.datum)}
      </p>

      <Fehler fehler={fehler} />

      {/*
        Was unterschrieben wird – knapp, aber vollständig: Auftrag, Arbeiten,
        Positionen mit Summe. Der Kunde liest das vor dem Namenszug.
      */}
      <dl className="karte grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-[max-content_1fr]">
        <dt className="text-sm text-slate-500">Auftrag</dt>
        <dd className="text-slate-900">
          {r.auftragNummer} {r.auftragBezeichnung}
        </dd>
        <dt className="text-sm text-slate-500">Techniker</dt>
        <dd className="text-slate-900">{r.mitarbeiter.name}</dd>
        {r.einsatzort && (
          <>
            <dt className="text-sm text-slate-500">Einsatzort</dt>
            <dd className="text-slate-900">{r.einsatzort}</dd>
          </>
        )}
        {r.taetigkeit && (
          <>
            <dt className="text-sm text-slate-500">Arbeiten</dt>
            <dd className="whitespace-pre-wrap text-slate-900">{r.taetigkeit}</dd>
          </>
        )}
      </dl>

      <div className="mt-3 mb-6">
        <Positionstabelle positionen={r.positionen} />
      </div>

      <p className="mb-2 text-slate-600">Bitte hier mit dem Finger oder Stift unterschreiben.</p>

      {/*
        Gestrichelt und nicht durchgezogen: Das Feld ist eine Aufforderung,
        keine Anzeige. Der Strich am unteren Rand ist die Linie, auf der man
        unterschreibt – ohne sie schwebt der Namenszug in der Fläche.
      */}
      <div className="mb-4 rounded border-2 border-dashed border-slate-300 bg-white p-2">
        <canvas
          ref={leinwand}
          className="block touch-none border-b border-slate-300 bg-white"
          onPointerDown={beginne}
          onPointerMove={ziehe}
          onPointerUp={beende}
          onPointerCancel={beende}
          onPointerLeave={beende}
        />
      </div>

      <div className="karte">
        <label className="feldname" htmlFor="unterzeichner">
          Name in Druckbuchstaben
        </label>
        <input
          id="unterzeichner"
          className="feld"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="knopf-haupt"
          disabled={leer || !name.trim() || laeuft}
          onClick={uebernehme}
        >
          Unterschrift übernehmen
        </button>
        <button type="button" onClick={loesche} disabled={leer} className="knopf">
          Neu zeichnen
        </button>
        <a
          href={`/rapport/${id}`}
          className="px-2 py-1 text-sm text-slate-500 underline underline-offset-4 hover:text-slate-900"
        >
          Abbrechen
        </a>
      </div>
    </>
  );
}
